/**
 * [Iter 420 — 2026-03-01] ProjectProcurementCard
 *
 * Same pattern as ProjectPickingCard + ProjectJobCardsCard: a tab-bar
 * of every procurement tracker linked to this project (direct +
 * family-tree via JC / picking / sales doc). The active tracker's
 * full items table + status + ETA renders inline. Users don't leave
 * the project to check on incoming stock — they only hit "Open full"
 * to make status changes.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Truck, ExternalLink, RefreshCw, Loader2, Package, CalendarClock,
  CheckCircle2,
} from "lucide-react";
import api from "../../lib/api";

const STATUS_COLOR = {
  draft: "text-white/80 bg-white/10",
  ordered: "text-cyan-300 bg-cyan-500/15",
  partially_received: "text-amber-300 bg-amber-500/15",
  complete: "text-emerald-300 bg-emerald-500/15",
  paused: "text-white/70 bg-white/10",
  cancelled: "text-rose-300 bg-rose-500/15",
  closed: "text-white/60 bg-white/[0.06]",
};

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");

export default function ProjectProcurementCard({ projectId }) {
  const [rows, setRows] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/procurement/trackers", {
        params: { project_id: projectId, limit: 200 },
      });
      const items = data?.items || [];
      setRows(items);
      if (items.length && !activeId) setActiveId(items[0].id);
      else if (!items.length) setActiveId(null);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadDetail = useCallback(async (tid) => {
    if (!tid) { setDetail(null); return; }
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/procurement/trackers/${tid}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadDetail(activeId); }, [activeId, loadDetail]);

  if (loading) {
    return (
      <div className="hv-card !p-3 flex items-center gap-2 text-[11px] text-[#647087]" data-testid="proj-procurement-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading procurement…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="hv-card !p-3 text-center" data-testid="proj-procurement-empty">
        <Truck className="w-6 h-6 mx-auto text-[#647087] mb-1.5" />
        <div className="text-[11px] text-[#647087] italic">
          No procurement trackers linked to this project yet.
        </div>
        <Link
          to={`/procurement?new=1&project_id=${projectId}`}
          className="hv-btn hv-btn-primary text-[11px] mt-2 inline-flex"
          data-testid="proj-procurement-new-empty"
        >
          <Truck className="w-3 h-3" /> Create tracker
        </Link>
      </div>
    );
  }

  const t = detail;
  const items = Array.isArray(t?.items) ? t.items : [];
  const totalOrdered = items.reduce((s, i) => s + (Number(i.qty_ordered) || 0), 0);
  const totalDelivered = items.reduce((s, i) => s + (Number(i.qty_delivered) || 0), 0);
  const totalOutstanding = Math.max(totalOrdered - totalDelivered, 0);
  const pct = totalOrdered > 0 ? Math.round((totalDelivered / totalOrdered) * 100) : 0;
  const statusCls = STATUS_COLOR[t?.status] || "text-[#a0aabf] bg-white/[0.06]";

  return (
    <div className="hv-card !p-3" data-testid="proj-procurement-card">
      {/* Header row */}
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="font-display font-semibold text-sm text-[#F59E0B] inline-flex items-center gap-1.5">
          <Truck className="w-3.5 h-3.5" /> Procurement
          <span className="text-[10px] text-[#647087] font-mono">· {rows.length} tracker{rows.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => { loadRows(); loadDetail(activeId); }}
            className="text-[10px] text-[#647087] hover:text-white inline-flex items-center gap-1"
            data-testid="proj-procurement-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <Link
            to={`/procurement?new=1&project_id=${projectId}`}
            className="hv-btn hv-btn-ghost text-[11px]"
            data-testid="proj-procurement-new"
          >
            + New tracker
          </Link>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex overflow-x-auto gap-1 mb-3 -mx-1 px-1 hv-scrollbar-thin">
        {rows.map((r) => {
          const label = r.name || r.id.slice(0, 12);
          const via = r.linked_via;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => setActiveId(r.id)}
              className={`shrink-0 px-2.5 py-1 rounded text-[11px] border ${
                activeId === r.id
                  ? "bg-[#F59E0B]/20 border-[#F59E0B] text-white"
                  : "bg-white/[0.02] border-white/5 text-[#a0aabf] hover:bg-white/[0.04]"
              }`}
              data-testid={`proj-proc-tab-${r.id}`}
            >
              {label}
              {r.status && <span className="ml-1 text-[9px] text-[#647087]">· {r.status}</span>}
              {via && via.kind && via.kind !== "direct" && (
                <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA]" title={`Linked via ${via.kind}`}>
                  via {via.kind === "picking_list" ? "picking" : via.kind === "jobcard" ? "JC" : via.kind === "sales_document" ? "sales doc" : via.kind}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Active tracker detail */}
      {loadingDetail && (
        <div className="flex items-center gap-2 text-[11px] text-[#647087]">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}

      {!loadingDetail && t && (
        <div className="space-y-3">
          {/* Sub-header */}
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-white truncate">{t.name}</div>
              <div className="text-[10px] text-[#647087] flex items-center gap-2 flex-wrap mt-0.5">
                {t.supplier_name && <span>{t.supplier_name}</span>}
                {t.default_expected_delivery_date && (
                  <span className="inline-flex items-center gap-0.5">
                    <CalendarClock className="w-2.5 h-2.5" /> ETA {fmtDate(t.default_expected_delivery_date)}
                  </span>
                )}
                {t.delivery_method && <span>· {t.delivery_method}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${statusCls}`} data-testid="proj-proc-status">
                {t.status || "—"}
              </span>
              <Link
                to={`/procurement/${t.id}`}
                className="hv-btn hv-btn-primary text-[11px]"
                data-testid={`proj-proc-open-full-${t.id}`}
              >
                <ExternalLink className="w-3 h-3" /> Open full
              </Link>
            </div>
          </div>

          {/* Progress */}
          {totalOrdered > 0 && (
            <div>
              <div className="flex items-center justify-between text-[10px] text-[#647087] mb-1">
                <span className="uppercase tracking-wider">Progress · {totalDelivered}/{totalOrdered} delivered</span>
                <span className="font-mono text-[#F59E0B]">{pct}%</span>
              </div>
              <div className="h-1.5 rounded bg-white/[0.06] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#F59E0B] to-[#10B981]"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}

          {/* Items table */}
          {items.length === 0 ? (
            <div className="text-[11px] text-[#647087] italic">No items on this tracker yet.</div>
          ) : (
            <div className="overflow-x-auto hv-scrollbar-thin">
              <table
                className="w-full text-[11px] min-w-[520px]"
                data-testid="proj-proc-items-table"
              >
                <thead>
                  <tr className="text-[9px] uppercase tracking-wider text-[#647087] border-b border-white/[0.06]">
                    <th className="text-left py-1.5 pr-2">Item</th>
                    <th className="text-right py-1.5 px-1.5">Ordered</th>
                    <th className="text-right py-1.5 px-1.5">Delivered</th>
                    <th className="text-right py-1.5 px-1.5">Outstanding</th>
                    <th className="text-right py-1.5 pl-1.5">ETA</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const ord = Number(it.qty_ordered) || 0;
                    const del = Number(it.qty_delivered) || 0;
                    const out = Math.max(ord - del, 0);
                    const isDone = ord > 0 && del >= ord;
                    return (
                      <tr
                        key={it.id || idx}
                        className={`border-b border-white/[0.03] ${isDone ? "bg-emerald-500/[0.03]" : ""}`}
                        data-testid={`proj-proc-item-${idx}`}
                      >
                        <td className="py-1.5 pr-2">
                          <div className="flex items-center gap-1.5">
                            {isDone ? (
                              <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                            ) : (
                              <Package className="w-3 h-3 text-[#647087] shrink-0" />
                            )}
                            <span className={`truncate ${isDone ? "text-white" : "text-[#a0aabf]"}`}>
                              {it.name || it.description || "(item)"}
                            </span>
                          </div>
                        </td>
                        <td className="py-1.5 px-1.5 text-right font-mono text-white">{ord}</td>
                        <td className="py-1.5 px-1.5 text-right font-mono text-emerald-300">{del}</td>
                        <td className={`py-1.5 px-1.5 text-right font-mono ${out > 0 ? "text-amber-300" : "text-[#647087]"}`}>
                          {out}
                        </td>
                        <td className="py-1.5 pl-1.5 text-right font-mono text-[10px] text-[#a0aabf]">
                          {fmtDate(it.expected_delivery_date)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-white/[0.08]">
                    <td className="py-1.5 pr-2 text-[10px] uppercase tracking-wider text-[#a0aabf] font-semibold">Totals</td>
                    <td className="py-1.5 px-1.5 text-right font-mono text-white">{totalOrdered}</td>
                    <td className="py-1.5 px-1.5 text-right font-mono text-emerald-300">{totalDelivered}</td>
                    <td className={`py-1.5 px-1.5 text-right font-mono ${totalOutstanding > 0 ? "text-amber-300" : "text-[#647087]"}`}>
                      {totalOutstanding}
                    </td>
                    <td className="py-1.5"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
